<template>
    <div class="analytics-dashboard container-fluid py-4">
        <!-- Header -->
        <div class="row mb-4">
            <div class="col-12">
                <h1 class="text-gradient mb-2">Fee Economics</h1>
                <p class="text-muted">Deep analysis of fees, revenue, efficiency, and transaction economics</p>
                <div class="d-flex gap-3 align-items-center flex-wrap mt-3">
                    <date-range-picker default-range="24h" @change="handleDateRangeChange" />
                    <granularity-toggle
                        v-model="granularityMode"
                        :interval="currentInterval"
                        @change="handleGranularityChange"
                    />
                    <currency-toggle
                        v-model="currencyMode"
                        :date-from="dateRange?.[0]"
                        :date-to="dateRange?.[1]"
                        @change="handleCurrencyChange"
                        @prices-loaded="handlePricesLoaded"
                    />
                    <div class="ms-auto text-muted small" v-if="lastUpdated">
                        Last updated: {{ lastUpdated }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Hero Stats -->
        <div class="analytics-hero mb-4">
            <div class="analytics-hero-stat">
                <div class="hero-label">Total Fees</div>
                <div class="hero-value">{{ formatDualCurrency(summary.totalFees).primary }}</div>
                <div class="hero-secondary">{{ formatDualCurrency(summary.totalFees).secondary }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--green">
                <div class="hero-label">Total Revenue</div>
                <div class="hero-value">{{ formatDualCurrency(summary.totalRevenue).primary }}</div>
                <div class="hero-secondary">{{ formatDualCurrency(summary.totalRevenue).secondary }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--amber">
                <div class="hero-label">Jito Tips</div>
                <div class="hero-value">{{ formatDualCurrency(summary.totalJitoTips).primary }}</div>
                <div class="hero-secondary">{{ formatDualCurrency(summary.totalJitoTips).secondary }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--blue">
                <div class="hero-label">Transactions</div>
                <div class="hero-value">{{ formatCompact(summary.totalTransactions) }}</div>
            </div>
        </div>

        <!-- SECTION: Fee Overview -->
        <div class="section-divider">
            <h2 class="section-title">Fee Overview</h2>
            <p class="section-subtitle">Base fees, priority fees, and Jito tips breakdown</p>
        </div>

        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Fee Breakdown Over Time</div>
                    <div class="panel-subtitle">Stacked by fee type</div>
                </div>
                <div class="panel-body">
                    <canvas ref="feeBreakdownChart" height="300"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Revenue Trend</div>
                    <div class="panel-subtitle">Total validator revenue (fees + tips)</div>
                </div>
                <div class="panel-body">
                    <canvas ref="revenueChart" height="300"></canvas>
                </div>
            </div>
        </div>

        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Transaction Volume vs Average Fee</div>
                    <div class="panel-subtitle">Fee shown in {{ currencyMode === 'sol' ? 'lamports' : 'USD' }}</div>
                </div>
                <div class="panel-body">
                    <canvas ref="txVsFeeChart" height="280"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">DeFi Activity</div>
                    <div class="panel-subtitle">Swaps and transfers count over time</div>
                </div>
                <div class="panel-body">
                    <canvas ref="defiActivityChart" height="280"></canvas>
                </div>
            </div>
        </div>

        <!-- DeFi Volume Section -->
        <div class="section-divider">
            <h2 class="section-title">DeFi Volume & Cost Efficiency</h2>
            <p class="section-subtitle">Swap volume over time and transaction cost relative to volume</p>
        </div>

        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Swap Volume Over Time (USD)</div>
                    <div class="panel-subtitle">Total DEX swap volume per time period</div>
                </div>
                <div class="panel-body">
                    <canvas ref="swapVolumeChart" height="250"></canvas>
                </div>
            </div>
        </div>

        <!-- SECTION: Congestion & Optimal Fees - Hidden (needs real-time data, not historical) -->

        <!-- SECTION: Network Fee Metrics -->
        <div class="section-divider">
            <h2 class="section-title">Network Fee Metrics</h2>
            <p class="section-subtitle">Transaction fees per compute unit, per write, and relative to swap activity</p>
        </div>

        <div class="analytics-row cols-5 mb-4">
            <div class="compass-stat-card compass-stat-card--compact stat-card--purple">
                <div class="stat-label">Fee per CU</div>
                <div class="stat-value">{{ formatHumanReadable(latestEfficiency.feePerCu) }}</div>
                <div class="stat-unit">lamports</div>
            </div>
            <div class="compass-stat-card compass-stat-card--compact stat-card--cyan">
                <div class="stat-label">Fee per Write</div>
                <div class="stat-value">{{ formatHumanReadable(latestEfficiency.feePerWrite) }}</div>
                <div class="stat-unit">lamports</div>
            </div>
            <div class="compass-stat-card compass-stat-card--compact stat-card--blue">
                <div class="stat-label">Revenue per CU</div>
                <div class="stat-value">{{ formatHumanReadable(latestEfficiency.revPerCu) }}</div>
                <div class="stat-unit">lamports</div>
            </div>
            <div class="compass-stat-card compass-stat-card--compact stat-card--pink">
                <div class="stat-label">Fees per $1M Swapped</div>
                <div class="stat-value">{{ feePerMillionVolume }}</div>
            </div>
            <div class="compass-stat-card compass-stat-card--compact stat-card--amber" :title="swapFeeBpsTooltip">
                <div class="stat-label">Fees / Swap Vol</div>
                <div class="stat-value">{{ swapFeeBps }} <span class="stat-pct">({{ swapFeePercent }})</span></div>
                <div class="stat-unit">basis points</div>
            </div>
        </div>

        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Fee Efficiency Trends</div>
                    <div class="panel-subtitle">Fee per CU and fee per write over time</div>
                </div>
                <div class="panel-body">
                    <canvas ref="efficiencyTrendChart" height="280"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Compute Efficiency</div>
                    <div class="panel-subtitle">CU used vs CU limit requested</div>
                </div>
                <div class="panel-body">
                    <canvas ref="cuEfficiencyChart" height="280"></canvas>
                </div>
            </div>
        </div>

        <!-- SECTION: Jito Analysis -->
        <div class="section-divider">
            <h2 class="section-title">Jito Bundle Analysis</h2>
            <p class="section-subtitle">MEV transactions, tips, and comparison with regular transactions</p>
        </div>

        <div class="analytics-row cols-5 mb-4">
            <div class="compass-stat-card compass-stat-card--compact stat-card--orange">
                <div class="stat-label">Jito Transactions</div>
                <div class="stat-value">{{ formatCompact(summary.jitoTransactions) }}</div>
            </div>
            <div class="compass-stat-card compass-stat-card--compact stat-card--cyan">
                <div class="stat-label">Network Share</div>
                <div class="stat-value">{{ summary.jitoPercent }}%</div>
            </div>
            <div class="compass-stat-card compass-stat-card--compact stat-card--amber">
                <div class="stat-label">Total Tips</div>
                <div class="stat-value">{{ formatDualCurrency(summary.totalJitoTips).primary }}</div>
                <div class="stat-unit">{{ formatDualCurrency(summary.totalJitoTips).secondary }}</div>
            </div>
            <div class="compass-stat-card compass-stat-card--compact stat-card--purple">
                <div class="stat-label">Avg Tip per Tx</div>
                <div class="stat-value">{{ formatDualCurrency(avgJitoTip / 1e9).primary }}</div>
                <div class="stat-unit">{{ formatDualCurrency(avgJitoTip / 1e9).secondary }}</div>
            </div>
            <div class="compass-stat-card compass-stat-card--compact stat-card--pink">
                <div class="stat-label">Median Tip per Tx</div>
                <div class="stat-value">{{ formatHumanReadable(aggregatedPercentiles.jitoTip?.[50] || 0) }}</div>
                <div class="stat-unit">lamports{{ medianTipUsd }}</div>
            </div>
        </div>

        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Jito Adoption Over Time</div>
                </div>
                <div class="panel-body">
                    <canvas ref="jitoAdoptionChart" height="280"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Jito Tips Over Time</div>
                </div>
                <div class="panel-body">
                    <canvas ref="jitoTipsChart" height="280"></canvas>
                </div>
            </div>
        </div>

        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Native Transactions vs Jito</div>
                </div>
                <div class="panel-body">
                    <table class="compass-table">
                        <thead><tr><th>Metric</th><th class="text-end">Native</th><th class="text-end">Jito</th><th class="text-end">Difference</th></tr></thead>
                        <tbody>
                            <tr>
                                <td>Transaction Count</td>
                                <td class="text-end">{{ formatCompact(nonJitoBreakdown.tx_count || 0) }}</td>
                                <td class="text-end">{{ formatCompact(jitoBreakdown.tx_count || 0) }}</td>
                                <td class="text-end text-gradient">{{ nativeVsJito('tx_count') }}</td>
                            </tr>
                            <tr>
                                <td>Average Fee</td>
                                <td class="text-end">{{ formatLamports(nonJitoBreakdown.fee?.avg || 0) }}</td>
                                <td class="text-end">{{ formatLamports(jitoBreakdown.fee?.avg || 0) }}</td>
                                <td class="text-end text-gradient">{{ nativeVsJito('fee_avg') }}</td>
                            </tr>
                            <tr>
                                <td>Avg Compute Used</td>
                                <td class="text-end">{{ formatCompact(nonJitoBreakdown.compute_units?.avg || 0) }}</td>
                                <td class="text-end">{{ formatCompact(jitoBreakdown.compute_units?.avg || 0) }}</td>
                                <td class="text-end text-gradient">{{ nativeVsJito('compute_avg') }}</td>
                            </tr>
                            <tr>
                                <td>Avg CPI Depth</td>
                                <td class="text-end">{{ (nonJitoBreakdown.cpi_depth?.avg || 0).toFixed(2) }}</td>
                                <td class="text-end">{{ (jitoBreakdown.cpi_depth?.avg || 0).toFixed(2) }}</td>
                                <td class="text-end text-gradient">{{ nativeVsJito('cpi_depth') }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Jito Tip Percentiles</div>
                </div>
                <div class="panel-body">
                    <table class="compass-table">
                        <thead><tr><th>Percentile</th><th class="text-end">Lamports</th><th class="text-end">SOL</th><th class="text-end">USD</th></tr></thead>
                        <tbody>
                            <tr v-for="p in [10, 25, 50, 75, 90, 95, 99]" :key="p">
                                <td>{{ p === 50 ? 'Median (p50)' : `p${p}` }}</td>
                                <td class="text-end">{{ formatCompact(aggregatedPercentiles.jitoTip?.[p] || 0) }}</td>
                                <td class="text-end">{{ lamportsToSol(aggregatedPercentiles.jitoTip?.[p] || 0) }}</td>
                                <td class="text-end text-gradient">{{ lamportsToUsd(aggregatedPercentiles.jitoTip?.[p] || 0) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- SECTION: Reverted Transactions -->
        <div class="section-divider">
            <h2 class="section-title">Reverted Transactions</h2>
            <p class="section-subtitle">Transactions that were processed but reverted (slippage, missed opportunities)</p>
        </div>

        <div class="analytics-row cols-4 mb-4">
            <div class="compass-stat-card compass-stat-card--compact stat-card--red">
                <div class="stat-label">Reverted Transactions</div>
                <div class="stat-value">{{ formatCompact(summary.revertedTransactions) }}</div>
            </div>
            <div class="compass-stat-card compass-stat-card--compact stat-card--orange">
                <div class="stat-label">Revert Rate</div>
                <div class="stat-value">{{ summary.revertedPercent }}%</div>
            </div>
            <div class="compass-stat-card compass-stat-card--compact stat-card--red">
                <div class="stat-label">Fees on Reverted</div>
                <div class="stat-value">{{ formatSOL(summary.revertedFees) }}</div>
            </div>
            <div class="compass-stat-card compass-stat-card--compact stat-card--amber">
                <div class="stat-label">Wasted Compute</div>
                <div class="stat-value">{{ formatCompact(failedBreakdown.compute_sum || 0) }}</div>
            </div>
        </div>

        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Reverted Transactions Over Time</div>
                </div>
                <div class="panel-body">
                    <canvas ref="revertedTrendChart" height="280"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Revert Rate Trend</div>
                </div>
                <div class="panel-body">
                    <canvas ref="revertRateChart" height="280"></canvas>
                </div>
            </div>
        </div>

        <!-- SECTION: Fee Distributions -->
        <div class="section-divider">
            <h2 class="section-title">Fee Distributions</h2>
            <p class="section-subtitle">Percentile breakdown of fees, compute, and accounts</p>
        </div>

        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Fee Distribution</div>
                </div>
                <div class="panel-body">
                    <table class="compass-table">
                        <thead><tr><th>Percentile</th><th class="text-end">Lamports</th><th class="text-end">SOL</th><th class="text-end">USD</th></tr></thead>
                        <tbody>
                            <tr v-for="p in [10, 25, 50, 75, 90, 95, 99]" :key="p">
                                <td>{{ p === 50 ? 'Median (p50)' : `p${p}` }}</td>
                                <td class="text-end">{{ formatCompact(aggregatedPercentiles.fee?.[p] || 0) }}</td>
                                <td class="text-end">{{ lamportsToSol(aggregatedPercentiles.fee?.[p] || 0) }}</td>
                                <td class="text-end text-gradient">{{ lamportsToUsd(aggregatedPercentiles.fee?.[p] || 0) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Priority Fee Distribution</div>
                </div>
                <div class="panel-body">
                    <table class="compass-table">
                        <thead><tr><th>Percentile</th><th class="text-end">Lamports</th><th class="text-end">SOL</th><th class="text-end">USD</th></tr></thead>
                        <tbody>
                            <tr v-for="p in [10, 25, 50, 75, 90, 95, 99]" :key="p">
                                <td>{{ p === 50 ? 'Median' : `p${p}` }}</td>
                                <td class="text-end">{{ formatCompact(aggregatedPercentiles.priorityFee?.[p] || 0) }}</td>
                                <td class="text-end">{{ lamportsToSol(aggregatedPercentiles.priorityFee?.[p] || 0) }}</td>
                                <td class="text-end text-gradient">{{ lamportsToUsd(aggregatedPercentiles.priorityFee?.[p] || 0) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Fee per Compute Unit</div>
                </div>
                <div class="panel-body">
                    <table class="compass-table">
                        <thead><tr><th>Percentile</th><th class="text-end">Fee/CU (micro-lamports)</th></tr></thead>
                        <tbody>
                            <tr v-for="p in [10, 25, 50, 75, 90, 95, 99]" :key="p">
                                <td>{{ p === 50 ? 'Median' : `p${p}` }}</td>
                                <td class="text-end">{{ formatMicro(distributions.feePerCu?.[p] || 0) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Compute Units per Transaction</div>
                </div>
                <div class="panel-body">
                    <table class="compass-table">
                        <thead><tr><th>Percentile</th><th class="text-end">Compute Units</th></tr></thead>
                        <tbody>
                            <tr v-for="p in [10, 25, 50, 75, 90, 95, 99]" :key="p">
                                <td>{{ p === 50 ? 'Median' : `p${p}` }}</td>
                                <td class="text-end">{{ formatCompact(distributions.computeUnits?.[p] || 0) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- SECTION: Top Transactions -->
        <div class="section-divider">
            <h2 class="section-title">Top Transactions</h2>
            <p class="section-subtitle">Highest fee and tip transactions in this period</p>
        </div>

        <div class="analytics-row cols-2 mb-4">
            <!-- Top by Fee -->
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Top by Fee</div>
                </div>
                <div class="panel-body p-0">
                    <div v-if="topTransactions.length > 0" class="table-responsive">
                        <table class="compass-table compass-table--compact">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th>Signature</th>
                                    <th class="text-end">Fee</th>
                                    <th class="text-end">Tip</th>
                                    <th class="text-end">Total</th>
                                    <th class="text-end">CU</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="tx in paginatedFeeTransactions" :key="tx.signature">
                                    <td class="text-muted small">{{ formatTxTime(tx.timestamp) }}</td>
                                    <td>
                                        <a :href="`https://solscan.io/tx/${tx.signature}`" target="_blank" class="text-gradient">
                                            {{ formatPubkey(tx.signature) }}
                                        </a>
                                    </td>
                                    <td class="text-end">{{ formatTxFee(tx.fee) }}</td>
                                    <td class="text-end">{{ formatTxFee(tx.tip) }}</td>
                                    <td class="text-end text-gradient fw-bold">{{ formatTxFee(tx.totalCost) }}</td>
                                    <td class="text-end">{{ formatCompact(tx.computeUnits) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-else class="text-muted text-center py-4">Loading transactions...</div>
                    <div v-if="topTransactions.length > txPageSize" class="table-pagination">
                        <button
                            class="pagination-btn"
                            :disabled="txFeePage === 1"
                            @click="txFeePage--"
                        >&laquo; Prev</button>
                        <span class="pagination-info">{{ txFeePage }} / {{ totalFeePages }}</span>
                        <button
                            class="pagination-btn"
                            :disabled="txFeePage >= totalFeePages"
                            @click="txFeePage++"
                        >Next &raquo;</button>
                    </div>
                </div>
            </div>

            <!-- Top by Tip -->
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Top by Jito Tip</div>
                </div>
                <div class="panel-body p-0">
                    <div v-if="topTransactionsByTip.length > 0" class="table-responsive">
                        <table class="compass-table compass-table--compact">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th>Signature</th>
                                    <th class="text-end">Tip</th>
                                    <th class="text-end">Fee</th>
                                    <th class="text-end">Total</th>
                                    <th class="text-end">CU</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="tx in paginatedTipTransactions" :key="tx.signature">
                                    <td class="text-muted small">{{ formatTxTime(tx.timestamp) }}</td>
                                    <td>
                                        <a :href="`https://solscan.io/tx/${tx.signature}`" target="_blank" class="text-gradient">
                                            {{ formatPubkey(tx.signature) }}
                                        </a>
                                    </td>
                                    <td class="text-end text-gradient fw-bold">{{ formatTxFee(tx.tip) }}</td>
                                    <td class="text-end">{{ formatTxFee(tx.fee) }}</td>
                                    <td class="text-end">{{ formatTxFee(tx.totalCost) }}</td>
                                    <td class="text-end">{{ formatCompact(tx.computeUnits) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-else class="text-muted text-center py-4">Loading transactions...</div>
                    <div v-if="topTransactionsByTip.length > txPageSize" class="table-pagination">
                        <button
                            class="pagination-btn"
                            :disabled="txTipPage === 1"
                            @click="txTipPage--"
                        >&laquo; Prev</button>
                        <span class="pagination-info">{{ txTipPage }} / {{ totalTipPages }}</span>
                        <button
                            class="pagination-btn"
                            :disabled="txTipPage >= totalTipPages"
                            @click="txTipPage++"
                        >Next &raquo;</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { getBarChartOptions, getLineChartOptions, getStackedBarChartOptions } from '../composables/useChartConfig';
import { GRANULARITY_MODES, transformValue, getUnitSuffix } from '../composables/useGranularityToggle';
import { CURRENCY_MODES, formatCurrencyValue, solToUsd, getCurrentPrice } from '../composables/useCurrencyToggle';

export default {
    name: 'FeeAnalytics',

    data() {
        return {
            currentRange: '24h',
            currentInterval: '1h',
            granularityMode: GRANULARITY_MODES.TOTAL,
            currencyMode: CURRENCY_MODES.SOL,
            solPrices: {},
            currentSolPrice: null,
            dateRange: null,
            summary: {},
            distributions: {},
            aggregatedPercentiles: {},
            jitoBreakdown: {},
            failedBreakdown: {},
            nonJitoBreakdown: {},
            successBreakdown: {},
            timeSeries: [],
            topTransactions: [],
            topTransactionsByTip: [],
            txFeePage: 1,
            txTipPage: 1,
            txPageSize: 10,
            lastUpdated: null,
            refreshInterval: null
        };
    },

    created() {
        this.charts = {};
    },

    computed: {
        latestEfficiency() {
            const last = this.timeSeries[this.timeSeries.length - 1] || {};
            return {
                feePerCu: last.feePerCu || 0,
                feePerWrite: last.feePerWrite || 0,
                revPerCu: last.revPerCu || 0
            };
        },
        // Calculate total swap volume across time series
        totalSwapVolume() {
            return this.timeSeries.reduce((sum, d) => sum + (d.swapVolume || 0), 0);
        },
        // Calculate total fees in USD for fee per volume metric
        totalFeesUsd() {
            const totalFeesSol = this.timeSeries.reduce((sum, d) => sum + (d.totalFees || 0), 0);
            return this.currentSolPrice ? totalFeesSol * this.currentSolPrice : totalFeesSol * 200; // fallback estimate
        },
        // Fee per million dollars of swap volume
        feePerMillionVolume() {
            if (!this.totalSwapVolume || this.totalSwapVolume === 0) return '-';
            const feePer1M = (this.totalFeesUsd / this.totalSwapVolume) * 1000000;
            if (feePer1M >= 1000) return '$' + (feePer1M / 1000).toFixed(1) + 'K';
            return '$' + feePer1M.toFixed(0);
        },
        // Swap fee as basis points (1 BPS = 0.01%)
        swapFeeBps() {
            if (!this.totalSwapVolume || this.totalSwapVolume === 0) return '-';
            const bps = (this.totalFeesUsd / this.totalSwapVolume) * 10000;
            if (bps >= 10) return bps.toFixed(1);
            if (bps >= 1) return bps.toFixed(2);
            return bps.toFixed(3);
        },
        // Percentage of swap volume that goes to fees
        swapFeePercent() {
            if (!this.totalSwapVolume || this.totalSwapVolume === 0) return '-';
            const bps = (this.totalFeesUsd / this.totalSwapVolume) * 10000;
            const percent = bps / 100;
            if (percent >= 0.01) return percent.toFixed(2) + '%';
            if (percent >= 0.001) return percent.toFixed(3) + '%';
            return percent.toFixed(4) + '%';
        },
        // USD equivalent of median Jito tip
        medianTipUsd() {
            const lamports = this.aggregatedPercentiles?.jitoTip?.[50] || 0;
            if (!lamports || !this.currentSolPrice) return '';
            const usd = (lamports / 1e9) * this.currentSolPrice;
            if (usd >= 1) return ' ($' + usd.toFixed(2) + ')';
            if (usd >= 0.01) return ' ($' + usd.toFixed(4) + ')';
            return ' ($' + usd.toFixed(6) + ')';
        },
        // Tooltip showing percentage equivalent
        swapFeeBpsTooltip() {
            if (!this.totalSwapVolume || this.totalSwapVolume === 0) return 'No swap volume data';
            const bps = (this.totalFeesUsd / this.totalSwapVolume) * 10000;
            const percent = bps / 100;
            return `${percent.toFixed(4)}% of swap volume goes to transaction fees`;
        },
        avgJitoTip() {
            if (!this.summary.jitoTransactions) return 0;
            return (this.summary.totalJitoTips * 1e9) / this.summary.jitoTransactions;
        },
        cuUtilization() {
            // Get average CU utilization from latest time series data
            const last = this.timeSeries[this.timeSeries.length - 1] || {};
            if (!last.totalComputeLimit || last.totalComputeLimit === 0) return 0;
            return Math.round((last.totalCompute / last.totalComputeLimit) * 100);
        },
        congestionLevel() {
            // Combine CU utilization and priority fee percentiles to calculate congestion
            // Weighted: 40% CU utilization, 60% priority fee pressure
            const cuUtil = this.cuUtilization;

            // Normalize priority fee p90 - typical ranges from 0 to 100k lamports
            // Higher p90 = more congestion
            const p90Fee = this.distributions.priorityFee?.[90] || 0;
            const feeNormalized = Math.min(100, (p90Fee / 50000) * 100); // 50k lamports = 100%

            return Math.min(100, Math.round(cuUtil * 0.4 + feeNormalized * 0.6));
        },
        congestionColor() {
            const level = this.congestionLevel;
            if (level < 30) return '#22c55e'; // Green - low
            if (level < 60) return '#f59e0b'; // Amber - moderate
            if (level < 80) return '#f97316'; // Orange - high
            return '#ef4444'; // Red - severe
        },
        congestionLabel() {
            const level = this.congestionLevel;
            if (level < 30) return 'Low Congestion';
            if (level < 60) return 'Moderate Congestion';
            if (level < 80) return 'High Congestion';
            return 'Severe Congestion';
        },
        feeDifferencePercent() {
            const completedFee = this.successBreakdown.fee?.avg || 0;
            const revertedFee = this.failedBreakdown.fee?.avg || 0;
            if (revertedFee === 0) return 0;
            return ((completedFee - revertedFee) / revertedFee) * 100;
        },
        feeDifferenceClass() {
            const diff = this.feeDifferencePercent;
            if (diff > 10) return 'text-success';
            if (diff < -10) return 'text-danger';
            return 'text-muted';
        },
        feeDifferenceText() {
            const diff = this.feeDifferencePercent;
            if (diff === 0) return 'No data';
            const sign = diff > 0 ? '+' : '';
            return `${sign}${diff.toFixed(1)}%`;
        },
        feeInsight() {
            const diff = this.feeDifferencePercent;
            if (!this.successBreakdown.fee?.avg && !this.failedBreakdown.fee?.avg) {
                return { icon: '...', text: 'Waiting for fee data to load...' };
            }
            if (diff > 20) {
                return {
                    icon: '!',
                    text: 'Completed transactions pay significantly higher fees. Paying above-average fees correlates with better landing rates.'
                };
            }
            if (diff > 5) {
                return {
                    icon: 'i',
                    text: 'Completed transactions pay slightly higher fees. Higher fees may marginally improve landing rates.'
                };
            }
            if (diff < -5) {
                return {
                    icon: '?',
                    text: 'Reverted transactions actually paid higher fees on average. Reverts are likely due to market conditions (slippage), not fee levels.'
                };
            }
            return {
                icon: '=',
                text: 'No significant fee difference between completed and reverted transactions. Fees alone do not determine landing success.'
            };
        },
        // Pagination computed properties for Top Transactions
        totalFeePages() {
            return Math.ceil(this.topTransactions.length / this.txPageSize);
        },
        totalTipPages() {
            return Math.ceil(this.topTransactionsByTip.length / this.txPageSize);
        },
        paginatedFeeTransactions() {
            const start = (this.txFeePage - 1) * this.txPageSize;
            return this.topTransactions.slice(start, start + this.txPageSize);
        },
        paginatedTipTransactions() {
            const start = (this.txTipPage - 1) * this.txPageSize;
            return this.topTransactionsByTip.slice(start, start + this.txPageSize);
        }
    },

    mounted() {
        this.$nextTick(() => {
            this.initCharts();
            this.loadData();
        });
        this.refreshInterval = setInterval(() => this.loadData(), 60000);
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(c => c?.destroy());
        if (this.refreshInterval) clearInterval(this.refreshInterval);
    },

    methods: {
        handleDateRangeChange(data) {
            this.currentRange = data.range;
            this.currentInterval = data.interval;
            this.loadData();
        },

        handleGranularityChange() {
            this.updateCharts();
        },

        handleCurrencyChange() {
            this.updateCharts();
        },

        handlePricesLoaded({ current, prices }) {
            this.currentSolPrice = current;
            this.solPrices = prices;
        },

        transformForGranularity(value) {
            return transformValue(value, this.granularityMode, this.currentInterval);
        },

        getGranularityLabel(baseLabel) {
            const suffix = getUnitSuffix(this.granularityMode, this.currentInterval, '');
            return suffix ? `${baseLabel} ${suffix}` : baseLabel;
        },

        formatFeeValue(solValue) {
            return formatCurrencyValue(solValue, this.currencyMode, this.currentSolPrice);
        },

        // Format value showing both currencies - primary based on toggle, secondary in brackets
        formatDualCurrency(solValue) {
            if (solValue === null || solValue === undefined) {
                return { primary: '-', secondary: '' };
            }
            const solFormatted = this.formatSolValue(solValue);
            const usdFormatted = this.currentSolPrice ? this.formatUsdValue(solValue * this.currentSolPrice) : null;

            if (this.currencyMode === CURRENCY_MODES.USD && usdFormatted) {
                return { primary: usdFormatted, secondary: `(${solFormatted})` };
            }
            return { primary: solFormatted, secondary: usdFormatted ? `(${usdFormatted})` : '' };
        },

        formatSolValue(v) {
            if (v >= 1e6) return (v / 1e6).toFixed(2) + 'M SOL';
            if (v >= 1e3) return (v / 1e3).toFixed(2) + 'K SOL';
            if (v >= 1) return v.toFixed(2) + ' SOL';
            return v.toFixed(4) + ' SOL';
        },

        formatUsdValue(v) {
            if (v >= 1e9) return '$' + (v / 1e9).toFixed(2) + 'B';
            if (v >= 1e6) return '$' + (v / 1e6).toFixed(2) + 'M';
            if (v >= 1e3) return '$' + (v / 1e3).toFixed(2) + 'K';
            return '$' + v.toFixed(2);
        },

        // Convert lamports to SOL with proper precision for display
        lamportsToSol(lamports) {
            if (!lamports) return '0';
            const sol = lamports / 1e9;
            if (sol >= 1) return sol.toFixed(4);
            if (sol >= 0.0001) return sol.toFixed(6);
            return sol.toFixed(9);
        },

        // Convert lamports to USD with proper formatting
        lamportsToUsd(lamports) {
            if (!lamports || !this.currentSolPrice) return '-';
            const sol = lamports / 1e9;
            const usd = sol * this.currentSolPrice;
            if (usd >= 1000) return '$' + (usd / 1000).toFixed(2) + 'K';
            if (usd >= 1) return '$' + usd.toFixed(2);
            if (usd >= 0.01) return '$' + usd.toFixed(4);
            return '$' + usd.toFixed(6);
        },

        // Format fee values for tooltips with proper precision and units
        formatFeeForTooltip(v) {
            if (v === null || v === undefined || isNaN(v)) return '-';
            if (this.currencyMode === CURRENCY_MODES.USD && this.currentSolPrice) {
                const usd = v * this.currentSolPrice;
                if (usd >= 1000) return '$' + (usd / 1000).toFixed(2) + 'K';
                if (usd >= 1) return '$' + usd.toFixed(2);
                if (usd >= 0.01) return '$' + usd.toFixed(4);
                return '$' + usd.toFixed(6);
            }
            // SOL - show with enough precision
            if (v >= 1000) return (v / 1000).toFixed(2) + 'K SOL';
            if (v >= 1) return v.toFixed(4) + ' SOL';
            if (v >= 0.0001) return v.toFixed(6) + ' SOL';
            if (v >= 0.00000001) return v.toFixed(9) + ' SOL';
            return v.toExponential(4) + ' SOL';
        },

        transformFeeForCurrency(solValue) {
            if (this.currencyMode === CURRENCY_MODES.USD && this.currentSolPrice) {
                return solToUsd(solValue, this.currentSolPrice);
            }
            return solValue;
        },

        // Transform fee values for charts - applies currency conversion
        transformFeeWithCurrency(solValue) {
            if (this.currencyMode === CURRENCY_MODES.USD && this.currentSolPrice) {
                return solToUsd(solValue, this.currentSolPrice);
            }
            return solValue;
        },

        initCharts() {
            // Use design system chart configurations

            this.charts.feeBreakdown = new Chart(this.$refs.feeBreakdownChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getStackedBarChartOptions({ title: 'SOL' })
            });

            this.charts.revenue = new Chart(this.$refs.revenueChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: getLineChartOptions({ title: 'SOL' })
            });

            this.charts.txVsFee = new Chart(this.$refs.txVsFeeChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: {
                    ...getLineChartOptions({ formatCallback: v => this.formatCompact(v) }),
                    scales: {
                        y1: { type: 'linear', position: 'left', title: { display: true, text: 'Transactions' }, ticks: { callback: v => this.formatCompact(v) }, min: 0 },
                        y2: { type: 'linear', position: 'right', title: { display: true, text: 'Fee (Lamports)' }, grid: { drawOnChartArea: false }, min: 0, ticks: { callback: v => this.formatHumanReadable(v) } }
                    },
                    plugins: {
                        tooltip: {
                            callbacks: {
                                label: (ctx) => {
                                    const label = ctx.dataset.label || '';
                                    const value = ctx.parsed.y;
                                    if (label.includes('Fee')) {
                                        return `${label}: ${this.formatTxVsFeeTooltip(value)}`;
                                    }
                                    return `${label}: ${this.formatCompact(value)}`;
                                }
                            }
                        }
                    }
                }
            });

            this.charts.defiActivity = new Chart(this.$refs.defiActivityChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({ formatCallback: v => this.formatCompact(v) })
            });

            this.charts.swapVolume = new Chart(this.$refs.swapVolumeChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: {
                    ...getLineChartOptions({}),
                    scales: {
                        y: {
                            min: 0,
                            title: { display: true, text: 'USD Volume', color: '#9ca3af' },
                            ticks: { callback: v => '$' + this.formatCompact(v) }
                        }
                    },
                    plugins: {
                        tooltip: {
                            callbacks: {
                                label: (ctx) => `Volume: $${this.formatCompact(ctx.parsed.y)}`
                            }
                        }
                    }
                }
            });

            this.charts.efficiencyTrend = new Chart(this.$refs.efficiencyTrendChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: {
                    ...getLineChartOptions({}),
                    scales: {
                        y1: {
                            type: 'linear',
                            position: 'left',
                            title: { display: true, text: 'Fee/CU (lamports)', color: '#10f7cf' },
                            ticks: { color: '#10f7cf', callback: v => this.formatHumanReadable(v) },
                            min: 0
                        },
                        y2: {
                            type: 'linear',
                            position: 'right',
                            title: { display: true, text: 'Fee/Write (lamports)', color: '#8b5cf6' },
                            ticks: { color: '#8b5cf6', callback: v => this.formatHumanReadable(v) },
                            grid: { drawOnChartArea: false },
                            min: 0
                        }
                    },
                    plugins: {
                        tooltip: {
                            callbacks: {
                                label: (ctx) => {
                                    const label = ctx.dataset.label || '';
                                    const value = ctx.parsed.y;
                                    return `${label}: ${this.formatHumanReadable(value)} lamports`;
                                }
                            }
                        }
                    }
                }
            });

            this.charts.cuEfficiency = new Chart(this.$refs.cuEfficiencyChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: {
                    ...getLineChartOptions({}),
                    scales: { y: { max: 100, min: 0, title: { display: true, text: '%' } } }
                }
            });

            this.charts.jitoAdoption = new Chart(this.$refs.jitoAdoptionChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: getLineChartOptions({ formatCallback: v => this.formatCompact(v) })
            });

            this.charts.jitoTips = new Chart(this.$refs.jitoTipsChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({})
            });

            this.charts.revertedTrend = new Chart(this.$refs.revertedTrendChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: {
                    ...getLineChartOptions({ formatCallback: v => this.formatCompact(v) }),
                    scales: {
                        y1: { type: 'linear', position: 'left', ticks: { callback: v => this.formatCompact(v) }, min: 0 },
                        y2: { type: 'linear', position: 'right', grid: { drawOnChartArea: false }, min: 0 }
                    }
                }
            });

            this.charts.revertRate = new Chart(this.$refs.revertRateChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: {
                    ...getLineChartOptions({}),
                    scales: { y: { min: 0, title: { display: true, text: '%' } } }
                }
            });
        },

        async loadData() {
            try {
                const res = await fetch(`/analytics/api/fee-metrics?range=${this.currentRange}&interval=${this.currentInterval}`);
                const data = await res.json();

                this.summary = data.summary || {};
                this.distributions = data.distributions || {};
                this.aggregatedPercentiles = data.aggregatedPercentiles || {};
                this.jitoBreakdown = data.jitoBreakdown || {};
                this.failedBreakdown = data.revertedBreakdown || {};
                this.nonJitoBreakdown = data.nonJitoBreakdown || {};
                this.successBreakdown = data.completedBreakdown || {};
                this.timeSeries = data.timeSeries || [];
                this.lastUpdated = new Date().toLocaleTimeString();

                this.updateCharts();
                this.loadTopTransactions();
            } catch (e) {
                console.error('Error loading data:', e);
            }
        },

        updateCharts() {
            const ts = this.timeSeries;
            const labels = ts.map(d => d.label);
            const transform = this.transformForGranularity.bind(this);
            const transformFee = this.transformFeeWithCurrency.bind(this);

            // Currency label for charts
            const currencyLabel = this.currencyMode === CURRENCY_MODES.USD ? 'USD' : 'SOL';

            // Fee charts - apply both granularity and currency transformation
            this.charts.feeBreakdown.data = {
                labels,
                datasets: [
                    { label: this.getGranularityLabel('Base Fees'), data: ts.map(d => transformFee(transform(d.baseFees))), backgroundColor: 'rgba(99, 102, 241, 0.8)' },
                    { label: this.getGranularityLabel('Priority Fees'), data: ts.map(d => transformFee(transform(d.priorityFees))), backgroundColor: 'rgba(139, 92, 246, 0.8)' },
                    { label: this.getGranularityLabel('Jito Tips'), data: ts.map(d => transformFee(transform(d.jitoTips))), backgroundColor: 'rgba(249, 115, 22, 0.8)' }
                ]
            };
            // Update Y-axis label based on currency
            if (!this.charts.feeBreakdown.options.scales.y.title) {
                this.charts.feeBreakdown.options.scales.y.title = { display: true, text: currencyLabel, color: '#9ca3af' };
            } else {
                this.charts.feeBreakdown.options.scales.y.title.text = currencyLabel;
            }
            this.charts.feeBreakdown.update();

            this.charts.revenue.data = {
                labels,
                datasets: [{ label: this.getGranularityLabel('Revenue'), data: ts.map(d => transformFee(transform(d.revenue))), borderColor: '#8b5cf6', backgroundColor: 'rgba(139, 92, 246, 0.1)', fill: true }]
            };
            if (!this.charts.revenue.options.scales.y.title) {
                this.charts.revenue.options.scales.y.title = { display: true, text: currencyLabel, color: '#9ca3af' };
            } else {
                this.charts.revenue.options.scales.y.title.text = currencyLabel;
            }
            this.charts.revenue.update();

            // Tx vs Fee chart - use per-bucket median fee so it fluctuates over time
            // When in SOL mode, convert to lamports for readability (no tiny decimals)
            const transformFeeForChart = this.transformFeeForTxVsFee.bind(this);
            const feeAxisLabel = this.currencyMode === CURRENCY_MODES.USD ? 'Fee (USD)' : 'Fee (Lamports)';
            this.charts.txVsFee.data = {
                labels,
                datasets: [
                    { label: this.getGranularityLabel('Transactions'), data: ts.map(d => transform(d.txCount)), borderColor: '#ec4899', yAxisID: 'y1' },
                    { label: 'Avg Fee', data: ts.map(d => transformFeeForChart(d.avgFee)), borderColor: '#3b82f6', yAxisID: 'y2' },
                    { label: 'Median Fee', data: ts.map(d => transformFeeForChart(d.medianFee)), borderColor: '#22c55e', borderDash: [5, 5], yAxisID: 'y2' }
                ]
            };
            if (this.charts.txVsFee.options.scales.y2) {
                if (!this.charts.txVsFee.options.scales.y2.title) {
                    this.charts.txVsFee.options.scales.y2.title = { display: true, text: feeAxisLabel, color: '#9ca3af' };
                } else {
                    this.charts.txVsFee.options.scales.y2.title.text = feeAxisLabel;
                }
            }
            this.charts.txVsFee.update();

            this.charts.defiActivity.data = {
                labels,
                datasets: [
                    { label: this.getGranularityLabel('Swaps'), data: ts.map(d => transform(d.numSwaps)), backgroundColor: 'rgba(16, 247, 207, 0.6)' },
                    { label: this.getGranularityLabel('Transfers'), data: ts.map(d => transform(d.numTransfers)), backgroundColor: 'rgba(139, 92, 246, 0.6)' }
                ]
            };
            this.charts.defiActivity.update();

            // Swap volume chart - USD volume over time
            this.charts.swapVolume.data = {
                labels,
                datasets: [{
                    label: this.getGranularityLabel('Swap Volume'),
                    data: ts.map(d => transform(d.swapVolume || 0)),
                    borderColor: '#ec4899',
                    backgroundColor: 'rgba(236, 72, 153, 0.15)',
                    fill: true,
                    tension: 0.3,
                    borderWidth: 2
                }]
            };
            this.charts.swapVolume.update();

            // Efficiency metrics are already per-unit ratios, don't transform
            // Using dual Y-axis since Fee/CU and Fee/Write have different scales
            this.charts.efficiencyTrend.data = {
                labels,
                datasets: [
                    { label: 'Fee/CU', data: ts.map(d => d.feePerCu), borderColor: '#10f7cf', yAxisID: 'y1' },
                    { label: 'Fee/Write', data: ts.map(d => d.feePerWrite), borderColor: '#8b5cf6', yAxisID: 'y2' }
                ]
            };
            this.charts.efficiencyTrend.update();

            // CU efficiency is a percentage, don't transform
            const cuEff = ts.map(d => d.totalComputeLimit > 0 ? (d.totalCompute / d.totalComputeLimit) * 100 : 0);
            this.charts.cuEfficiency.data = {
                labels,
                datasets: [{ label: 'CU Efficiency', data: cuEff, borderColor: '#f59e0b', backgroundColor: 'rgba(245, 158, 11, 0.1)', fill: true }]
            };
            this.charts.cuEfficiency.update();

            this.charts.jitoAdoption.data = {
                labels,
                datasets: [
                    { label: this.getGranularityLabel('Total Tx'), data: ts.map(d => transform(d.txCount)), borderColor: '#8b5cf6' },
                    { label: this.getGranularityLabel('Jito Tx'), data: ts.map(d => transform(d.jitoTxCount)), borderColor: '#f97316', backgroundColor: 'rgba(249, 115, 22, 0.2)', fill: true }
                ]
            };
            this.charts.jitoAdoption.update();

            this.charts.jitoTips.data = {
                labels,
                datasets: [{ label: this.getGranularityLabel('Tips'), data: ts.map(d => transformFee(transform(d.jitoTips))), backgroundColor: 'rgba(249, 115, 22, 0.6)' }]
            };
            if (!this.charts.jitoTips.options.scales.y.title) {
                this.charts.jitoTips.options.scales.y.title = { display: true, text: currencyLabel, color: '#9ca3af' };
            } else {
                this.charts.jitoTips.options.scales.y.title.text = currencyLabel;
            }
            this.charts.jitoTips.update();

            this.charts.revertedTrend.data = {
                labels,
                datasets: [
                    { label: this.getGranularityLabel('Reverted Tx'), data: ts.map(d => transform(d.revertedTxCount)), borderColor: '#ef4444', yAxisID: 'y1' },
                    { label: this.getGranularityLabel('Reverted Fees'), data: ts.map(d => transform(d.revertedFees)), borderColor: '#f97316', yAxisID: 'y2' }
                ]
            };
            this.charts.revertedTrend.update();

            // Revert rate is a percentage, don't transform
            this.charts.revertRate.data = {
                labels,
                datasets: [{ label: 'Revert Rate', data: ts.map(d => d.revertedPercent), borderColor: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.1)', fill: true }]
            };
            this.charts.revertRate.update();
        },

        async loadTopTransactions() {
            // Reset pagination
            this.txFeePage = 1;
            this.txTipPage = 1;

            try {
                // Load top by fee and top by tip in parallel
                const [feeRes, tipRes] = await Promise.all([
                    fetch(`/analytics/api/top-transactions?range=${this.currentRange}&limit=50&sortBy=fee`),
                    fetch(`/analytics/api/top-transactions?range=${this.currentRange}&limit=50&sortBy=jito_tip`)
                ]);

                const [feeData, tipData] = await Promise.all([feeRes.json(), tipRes.json()]);

                this.topTransactions = feeData.transactions || [];
                this.topTransactionsByTip = tipData.transactions || [];
            } catch (e) { console.error(e); }
        },

        nativeVsJito(metric) {
            let nVal = 0, jVal = 0;
            switch (metric) {
                case 'tx_count': nVal = this.nonJitoBreakdown.tx_count || 0; jVal = this.jitoBreakdown.tx_count || 0; break;
                case 'fee_avg': nVal = this.nonJitoBreakdown.fee?.avg || 0; jVal = this.jitoBreakdown.fee?.avg || 0; break;
                case 'compute_avg': nVal = this.nonJitoBreakdown.compute_units?.avg || 0; jVal = this.jitoBreakdown.compute_units?.avg || 0; break;
                case 'cpi_depth': nVal = this.nonJitoBreakdown.cpi_depth?.avg || 0; jVal = this.jitoBreakdown.cpi_depth?.avg || 0; break;
            }
            if (jVal === 0) return '-';
            const diff = ((nVal - jVal) / jVal) * 100;
            return (diff >= 0 ? '+' : '') + diff.toFixed(1) + '%';
        },

        getEfficiencyClass(eff) {
            if (eff > 70) return 'text-success';
            if (eff > 40) return 'text-warning';
            return 'text-danger';
        },

        formatSOL(v) { if (!v) return '0 SOL'; if (v >= 1000) return (v / 1000).toFixed(1) + 'K SOL'; return v.toFixed(2) + ' SOL'; },
        formatCompact(v) { if (!v) return '0'; if (v >= 1e9) return (v / 1e9).toFixed(2) + 'B'; if (v >= 1e6) return (v / 1e6).toFixed(2) + 'M'; if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K'; return v.toLocaleString(); },
        formatLamports(v) { if (!v) return '0'; if (v >= 1e9) return (v / 1e9).toFixed(4) + ' SOL'; if (v >= 1e6) return (v / 1e6).toFixed(1) + 'M'; if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K'; return Math.round(v).toLocaleString(); },
        // Human-readable format for lamport values - consistent K/M/B formatting
        formatHumanReadable(v) {
            if (v === 0 || v === null || v === undefined) return '0';
            if (v >= 1e9) return (v / 1e9).toFixed(1) + 'B';
            if (v >= 1e6) return (v / 1e6).toFixed(1) + 'M';
            if (v >= 1e3) return (v / 1e3).toFixed(0) + 'K';
            if (v >= 1) return v.toFixed(1);
            if (v >= 0.001) return v.toFixed(3);
            if (v >= 0.000001) return v.toFixed(6);
            return '0';
        },
        formatMicro(v) { return (v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }); },
        formatPubkey(a) { return a && a.length > 12 ? `${a.slice(0, 4)}..${a.slice(-4)}` : a; },
        formatTxTime(timestamp) {
            if (!timestamp) return '-';
            const d = new Date(timestamp);
            return d.toLocaleString(undefined, {
                month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit'
            });
        },
        formatTxFee(solValue) {
            if (this.currencyMode === CURRENCY_MODES.USD && this.currentSolPrice) {
                const usdValue = solToUsd(solValue, this.currentSolPrice);
                return '$' + usdValue.toFixed(2);
            }
            return solValue.toFixed(4);
        },
        // Format fee for Tx vs Fee chart - shows lamports when in SOL mode, USD when in USD mode
        formatTxVsFeeTooltip(value) {
            if (this.currencyMode === CURRENCY_MODES.USD) {
                if (value >= 1) return '$' + value.toFixed(2);
                return '$' + value.toFixed(4);
            }
            // Value is in lamports when in SOL mode
            return this.formatHumanReadable(value) + ' lamports';
        },
        // Transform SOL fee value for Tx vs Fee chart
        transformFeeForTxVsFee(solValue) {
            if (this.currencyMode === CURRENCY_MODES.USD && this.currentSolPrice) {
                return solToUsd(solValue, this.currentSolPrice);
            }
            // Convert SOL to lamports for better readability
            return solValue * 1e9;
        }
    }
};
</script>

<style scoped>
/* Component-specific styles - common styles are in _compass-analytics.scss */

/* Congestion Indicator */
.congestion-indicator {
    padding: 1rem;
}

.congestion-gauge {
    width: 100%;
    height: 24px;
    background: rgba(139, 92, 246, 0.1);
    border-radius: 12px;
    overflow: hidden;
    position: relative;
}

.dark-mode .congestion-gauge {
    background: rgba(255, 255, 255, 0.1);
}

.congestion-fill {
    height: 100%;
    border-radius: 12px;
    transition: width 0.5s ease, background 0.5s ease;
}

.congestion-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.75rem;
}

.congestion-level {
    font-size: 1.25rem;
    font-weight: 600;
}

.congestion-percent {
    font-size: 2rem;
    font-weight: 700;
    background: linear-gradient(135deg, var(--compass-cyan, #10f7cf), var(--compass-purple, #8b5cf6));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
}

.congestion-factors {
    display: grid;
    gap: 0.75rem;
}

.congestion-factor {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem;
    background: rgba(139, 92, 246, 0.05);
    border-radius: 8px;
}

.dark-mode .congestion-factor {
    background: rgba(255, 255, 255, 0.05);
}

.factor-label {
    color: #6b7280;
    font-size: 0.875rem;
}

.dark-mode .factor-label {
    color: #9ca3af;
}

.factor-value {
    font-weight: 500;
}

/* Optimal Fee Calculator */
.optimal-fee-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
}

.optimal-fee-card {
    background: rgba(139, 92, 246, 0.05);
    border: 1px solid rgba(139, 92, 246, 0.1);
    border-radius: 12px;
    padding: 1rem;
    text-align: center;
    transition: all 0.3s ease;
}

.dark-mode .optimal-fee-card {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
}

.optimal-fee-card:hover {
    border-color: rgba(139, 92, 246, 0.3);
    transform: translateY(-2px);
}

.dark-mode .optimal-fee-card:hover {
    border-color: rgba(255, 255, 255, 0.2);
}

.optimal-fee-card.recommended {
    background: linear-gradient(135deg, rgba(16, 247, 207, 0.15), rgba(34, 197, 94, 0.15));
    border-color: var(--compass-cyan, #10f7cf);
}

.optimal-fee-card.urgent {
    background: linear-gradient(135deg, rgba(249, 115, 22, 0.15), rgba(239, 68, 68, 0.15));
    border-color: #f97316;
}

.optimal-target {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
    margin-bottom: 0.5rem;
}

.dark-mode .optimal-target {
    color: #9ca3af;
}

.optimal-fee-card.recommended .optimal-target {
    color: var(--compass-cyan, #10f7cf);
}

.optimal-fee-card.urgent .optimal-target {
    color: #f97316;
}

.optimal-value {
    font-size: 1.5rem;
    font-weight: 700;
    background: linear-gradient(135deg, var(--compass-cyan, #10f7cf), var(--compass-purple, #8b5cf6));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
}

.optimal-unit {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.25rem;
}

.dark-mode .optimal-unit {
    color: #9ca3af;
}

.optimal-note {
    font-size: 0.875rem;
    color: #6b7280;
    padding: 0.75rem;
    background: rgba(139, 92, 246, 0.05);
    border-radius: 8px;
}

.dark-mode .optimal-note {
    color: #9ca3af;
    background: rgba(255, 255, 255, 0.05);
}

.optimal-note p {
    margin: 0;
}

/* Fee Comparison */
.fee-comparison-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
}

.fee-comparison-stat {
    background: rgba(139, 92, 246, 0.05);
    border-radius: 8px;
    padding: 1rem;
    text-align: center;
}

.dark-mode .fee-comparison-stat {
    background: rgba(255, 255, 255, 0.05);
}

.fee-comparison-stat.span-2 {
    grid-column: span 2;
}

.text-amber { color: #f59e0b; }

.fee-insight {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem;
    background: rgba(139, 92, 246, 0.1);
    border-radius: 8px;
    border-left: 3px solid var(--compass-purple, #8b5cf6);
}

.insight-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--compass-purple, #8b5cf6);
    color: white;
    border-radius: 50%;
    font-weight: 700;
    font-size: 0.875rem;
    flex-shrink: 0;
}

.insight-text {
    font-size: 0.875rem;
    line-height: 1.5;
    color: #4b5563;
}

.dark-mode .insight-text {
    color: #d1d5db;
}

/* Compact Table Styles */
.compass-table--compact {
    font-size: 0.8125rem;
}

.compass-table--compact th,
.compass-table--compact td {
    padding: 0.5rem 0.625rem;
}

.compass-table--compact th:first-child,
.compass-table--compact td:first-child {
    padding-left: 1rem;
}

.compass-table--compact th:last-child,
.compass-table--compact td:last-child {
    padding-right: 1rem;
}

/* Table Pagination */
.table-pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 1rem;
    border-top: 1px solid rgba(139, 92, 246, 0.1);
    background: rgba(139, 92, 246, 0.02);
}

.dark-mode .table-pagination {
    border-top-color: rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.02);
}

.pagination-btn {
    background: transparent;
    border: 1px solid rgba(139, 92, 246, 0.3);
    color: var(--compass-purple, #8b5cf6);
    padding: 0.375rem 0.75rem;
    border-radius: 4px;
    font-size: 0.8125rem;
    cursor: pointer;
    transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
    background: rgba(139, 92, 246, 0.1);
    border-color: var(--compass-purple, #8b5cf6);
}

.pagination-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.pagination-info {
    font-size: 0.8125rem;
    color: #6b7280;
}

.dark-mode .pagination-info {
    color: #9ca3af;
}

/* Stat Card Color Variants */
.stat-card--purple {
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(139, 92, 246, 0.04) 100%) !important;
    border-color: rgba(139, 92, 246, 0.25) !important;
}
.stat-card--purple::before { background: linear-gradient(90deg, #8b5cf6, #a78bfa) !important; }

.stat-card--cyan {
    background: linear-gradient(135deg, rgba(16, 247, 207, 0.1) 0%, rgba(16, 247, 207, 0.03) 100%) !important;
    border-color: rgba(16, 247, 207, 0.25) !important;
}
.stat-card--cyan::before { background: linear-gradient(90deg, #10f7cf, #5eead4) !important; }

.stat-card--blue {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.04) 100%) !important;
    border-color: rgba(59, 130, 246, 0.25) !important;
}
.stat-card--blue::before { background: linear-gradient(90deg, #3b82f6, #60a5fa) !important; }

.stat-card--pink {
    background: linear-gradient(135deg, rgba(236, 72, 153, 0.12) 0%, rgba(236, 72, 153, 0.04) 100%) !important;
    border-color: rgba(236, 72, 153, 0.25) !important;
}
.stat-card--pink::before { background: linear-gradient(90deg, #ec4899, #f472b6) !important; }

.stat-card--amber {
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(245, 158, 11, 0.04) 100%) !important;
    border-color: rgba(245, 158, 11, 0.25) !important;
}
.stat-card--amber::before { background: linear-gradient(90deg, #f59e0b, #fbbf24) !important; }

.stat-card--orange {
    background: linear-gradient(135deg, rgba(249, 115, 22, 0.12) 0%, rgba(249, 115, 22, 0.04) 100%) !important;
    border-color: rgba(249, 115, 22, 0.25) !important;
}
.stat-card--orange::before { background: linear-gradient(90deg, #f97316, #fb923c) !important; }

.stat-card--red {
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(239, 68, 68, 0.04) 100%) !important;
    border-color: rgba(239, 68, 68, 0.25) !important;
}
.stat-card--red::before { background: linear-gradient(90deg, #ef4444, #f87171) !important; }

/* Percentage display next to BPS */
.stat-pct {
    font-size: 0.6em;
    font-weight: 500;
    background: none !important;
    -webkit-background-clip: unset !important;
    -webkit-text-fill-color: #9ca3af !important;
    color: #9ca3af !important;
}
.dark-mode .stat-pct {
    -webkit-text-fill-color: #6b7280 !important;
    color: #6b7280 !important;
}
</style>
